import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import CargomaticModal from 'components/v2/modal';
import SimpleCard from 'components/v2/SimpleCard';
import CargomaticInput from 'components/v2/input';
import Select from 'components/v2/select';
import Spinner from 'components/ui/Spinner';
import formatMoney from 'utils/formatters/Money';
import { showToast } from 'stores/ToastStore';
import { fetchCreditMemoReasonCodes } from 'utils/credit-memo';
import { BadgeAlt as Badge, Switch, Label, Table, TableHeader, TableHead, TableBody, TableFooter, TableRow, TableCell, } from '@cargomatic/ui';
function flattenLineItems(invoice) {
    var _a, _b, _c;
    const items = [];
    for (const group of (_a = invoice.lineItemGroups) !== null && _a !== void 0 ? _a : []) {
        for (const lineItem of (_b = group.lineItems) !== null && _b !== void 0 ? _b : []) {
            items.push(Object.assign(Object.assign({}, lineItem), { shipment_id: group.shipment_id, groupedChargeTypes: (_c = group.groupedChargeTypes) !== null && _c !== void 0 ? _c : [] }));
        }
    }
    return items;
}
export default function CreditMemoV2Modal({ showModal, invoice, disableSave, onSave, onClose, }) {
    const { t } = useTranslation();
    const [rebillRequired, setRebillRequired] = useState(true);
    const [adjustedItemsDetails, setAdjustedItemsDetails] = useState([]);
    const [errors, setErrors] = useState([]);
    const [creditTotal, setCreditTotal] = useState(0);
    const [reasonCodes, setReasonCodes] = useState([]);
    const allLineItems = flattenLineItems(invoice);
    const nonShipperCostItems = allLineItems.filter((item) => item.type !== 'shipper_cost');
    useEffect(() => {
        const loadReasonCodes = async () => {
            try {
                const result = await fetchCreditMemoReasonCodes();
                if (result && result.length) {
                    setReasonCodes(result);
                }
                else {
                    showToast(t('NOT_FOUND_REASON_CODES'), '', 'error');
                }
            }
            catch (e) {
                console.error(e);
                showToast(t('FAILED_FETCH_REASON_CODES'), '', 'error');
            }
        };
        loadReasonCodes();
    }, []);
    useEffect(() => {
        if (!showModal)
            return;
        const items = flattenLineItems(invoice);
        setAdjustedItemsDetails(items.map((item) => {
            const amount = item.type === 'shipper_cost'
                ? item.value || 0
                : item.unit_value || item.value || 0;
            const message = item.charge_code === 'FSC'
                ? 'Fuel Surcharge adjustment'
                : item.message;
            return Object.assign(Object.assign({}, item), { reasonCode: '', notes: '', amount, units: item.unit_qty || 1, billing_type: 'unchanged', total_amount: amount * (item.unit_qty || 1), message });
        }));
        setErrors(items.map((item) => ({
            _id: item._id,
            type: item.type,
            reasonCode: null,
            notes: null,
            amount: null,
            units: null,
        })));
        setCreditTotal(items.reduce((acc, curr) => acc + (Number(curr.value) || 0), 0));
    }, [showModal, invoice === null || invoice === void 0 ? void 0 : invoice._id]);
    const getError = (type, _id) => {
        const errorItem = errors.find((item) => item._id === _id);
        return errorItem ? errorItem[type] : null;
    };
    const getValue = (type, _id) => {
        const item = adjustedItemsDetails.find((d) => d._id === _id);
        return item ? item[type] : undefined;
    };
    function runValidationChecks() {
        const computedErrors = errors.map((errorItem) => {
            const item = adjustedItemsDetails.find((d) => d._id === errorItem._id);
            if (!item)
                return Object.assign({}, errorItem);
            const updated = Object.assign({}, errorItem);
            updated.amount = item.amount < 0 ? t('CREDIT_MEMO_AMOUNT_ERROR') : null;
            if (item.type === 'shipper_cost') {
                // Notes and reason code are optional for shipper_cost; only validate max length
                updated.notes =
                    item.notes.trim().length > 100
                        ? t('CREDIT_MEMO_NOTES_CHARACTER_LENGTH_ERROR')
                        : null;
                updated.reasonCode = null;
            }
            else if (item.notes.trim().length <= 0) {
                updated.notes = t('EMPTY_NOTES_ERROR');
            }
            else if (item.notes.trim().length > 100) {
                updated.notes = t('CREDIT_MEMO_NOTES_CHARACTER_LENGTH_ERROR');
            }
            else {
                updated.notes = null;
            }
            updated.reasonCode = !item.reasonCode ? t('REQUIRED_REASON_CODE') : null;
            updated.units =
                !item.units || item.units < 0 ? t('CREDIT_MEMO_UNITS_ERROR') : null;
            return updated;
        });
        setErrors(computedErrors);
        return computedErrors;
    }
    function buildSavePayload() {
        var _a, _b, _c;
        const orderId = typeof invoice.order === 'string'
            ? invoice.order
            : ((_b = (_a = invoice.order) === null || _a === void 0 ? void 0 : _a._id) !== null && _b !== void 0 ? _b : '');
        // Split adjusted items into shipment-level vs order-level
        const selectedCharges = {};
        const selectedOrderCharges = [];
        for (const item of adjustedItemsDetails) {
            if (item.shipment_id) {
                if (!selectedCharges[item.shipment_id]) {
                    selectedCharges[item.shipment_id] = {
                        selected: [],
                        groupedChargeTypes: (_c = item.groupedChargeTypes) !== null && _c !== void 0 ? _c : [],
                    };
                }
                selectedCharges[item.shipment_id].selected.push(item._id);
            }
            else {
                selectedOrderCharges.push(item._id);
            }
        }
        const allAdjustedItems = adjustedItemsDetails.map((item) => {
            var _a;
            return (Object.assign(Object.assign({}, item), { notes: ((_a = item.notes) !== null && _a !== void 0 ? _a : '').trim() }));
        });
        return {
            rebill: rebillRequired,
            adjustedItems: allAdjustedItems,
            invoiceId: invoice._id,
            invoiceDate: invoice.date_created || new Date().toISOString(),
            orderId,
            selectedCharges,
            selectedOrderCharges,
            sendEdi: true,
        };
    }
    function handleSave() {
        const computedErrors = runValidationChecks();
        const hasErrors = computedErrors.some((item) => item.reasonCode || item.amount || item.notes || item.units);
        if (hasErrors)
            return;
        onSave(buildSavePayload());
    }
    const onChange = (type, _id, value) => {
        setAdjustedItemsDetails((previous) => {
            const prev = [...previous];
            const index = prev.findIndex((item) => item._id.toString() === _id.toString());
            const original = allLineItems.find((item) => item._id === _id);
            if (!original || index === -1)
                return prev;
            if (type === 'billing_type') {
                let units = 0;
                if (value === 'changed') {
                    units = prev[index].units;
                }
                if (value === 'unchanged') {
                    units = original.unit_qty || 1;
                    prev[index].amount = original.unit_value || original.value || 0;
                }
                prev[index].units = units;
                prev[index].total_amount = prev[index].amount * units;
            }
            prev[index][type] = value;
            if (prev[index].amount === (original.unit_value || original.value || 0) &&
                prev[index].units === (original.unit_qty || 1)) {
                prev[index].billing_type = 'unchanged';
            }
            else {
                prev[index].billing_type = 'changed';
            }
            prev[index].total_amount = prev[index].amount * prev[index].units;
            return prev;
        });
    };
    const renderLineItem = (index, lineItem) => {
        const isOrderLevel = !lineItem.shipment_id;
        return (React.createElement(TableRow, { "data-testid": `tr-cm-v2-${index}`, key: lineItem._id, className: isOrderLevel ? 'bg-muted/30' : undefined },
            React.createElement(TableCell, { className: "align-top whitespace-nowrap" }, isOrderLevel ? (React.createElement("div", { className: "flex items-center gap-2" },
                lineItem.message,
                React.createElement(Badge, { className: "shrink-0 whitespace-nowrap bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-200" }, "Order-Level"))) : (lineItem.message)),
            React.createElement(TableCell, { className: "align-top" }, reasonCodes.length > 0 ? (React.createElement(Select, { dataTestId: `Select-cm-v2-reason-${index}`, selected: getValue('reasonCode', lineItem._id), onSelect: ({ description: value }) => onChange('reasonCode', lineItem._id, value), items: reasonCodes, valueKey: "description", textKey: "description", caption: t('REASON_CODE'), hideLabel: true, error: { message: getError('reasonCode', lineItem._id) } })) : (React.createElement(Spinner, { showMessage: false, showLarge: false }))),
            React.createElement(TableCell, { className: "align-top" },
                React.createElement(CargomaticInput, { id: `cm-v2-notes-${index}`, type: "text", value: getValue('notes', lineItem._id), placeholder: t('ENTER_NOTE_PLACEHOLDER'), onChange: (value) => onChange('notes', lineItem._id, value), hideLabel: true, error: { message: getError('notes', lineItem._id) } })),
            rebillRequired && (React.createElement(React.Fragment, null,
                React.createElement(TableCell, { className: "align-top" },
                    React.createElement(CargomaticInput, { id: `cm-v2-amount-${index}`, type: "number", value: getValue('amount', lineItem._id), placeholder: lineItem.type === 'shipper_cost'
                            ? t('NEW_DOLLAR_VALUE_PLACEHOLDER')
                            : t('DOLLAR_RATE_PER_UNIT'), onChange: (value) => onChange('amount', lineItem._id, parseFloat(value)), hideLabel: true, error: { message: getError('amount', lineItem._id) } })),
                React.createElement(TableCell, { className: "align-top" }, lineItem.type !== 'shipper_cost' ? (React.createElement(CargomaticInput, { id: `cm-v2-units-${index}`, type: "number", placeholder: t('UNIT', { count: 5 }), value: getValue('units', lineItem._id), onChange: (value) => onChange('units', lineItem._id, parseFloat(value)), disabled: !lineItem.accessorial_request_id, hideLabel: true, error: { message: getError('units', lineItem._id) } })) : null),
                React.createElement(TableCell, { className: "align-top text-right whitespace-nowrap" }, lineItem.type === 'shipper_cost'
                    ? formatMoney(getValue('amount', lineItem._id))
                    : (lineItem.accessorial_request_id ||
                        lineItem.charge_code === 'FSC') &&
                        formatMoney(getValue('total_amount', lineItem._id)))))));
    };
    const renderHeaders = () => (React.createElement(TableRow, null,
        React.createElement(TableHead, { className: "whitespace-nowrap" }, t('CHARGE')),
        React.createElement(TableHead, { className: "whitespace-nowrap" }, t('REASON_CODE')),
        React.createElement(TableHead, { className: "whitespace-nowrap" }, titleize(t('NOTE', { count: 4 }))),
        nonShipperCostItems.length > 0 && rebillRequired ? (React.createElement(React.Fragment, null,
            React.createElement(TableHead, { className: "whitespace-nowrap" }, t('RATE_IN_DOLLAR')),
            React.createElement(TableHead, { className: "whitespace-nowrap" }, t('UNIT', { count: 5 })),
            React.createElement(TableHead, { className: "whitespace-nowrap text-right" }, t('TOTAL')))) : (React.createElement(React.Fragment, null,
            React.createElement(TableHead, null),
            React.createElement(TableHead, null),
            React.createElement(TableHead, null)))));
    const renderRebillTotal = () => {
        const rebillTotal = adjustedItemsDetails.reduce((total, item) => total + item.total_amount, 0);
        return (React.createElement(TableRow, null,
            React.createElement(TableCell, { colSpan: 5, className: "text-right whitespace-nowrap" },
                React.createElement("span", { className: "font-medium" }, t('REBILL_TOTAL'))),
            React.createElement(TableCell, { className: "text-right whitespace-nowrap" },
                React.createElement("span", null, formatMoney(rebillTotal)))));
    };
    const renderModalBody = () => (React.createElement("div", null,
        React.createElement("div", { className: "grid grid-cols-2 gap-5 max-w-md" },
            React.createElement(SimpleCard, { dataTestId: "SimpleCard-cm-v2-invoice", className: "", title: t('CREDIT_INVOICE'), key: "invoice-number" },
                React.createElement("p", { className: "font-medium mt-1 text-gray-900 tracking-tight text-base" }, invoice.number)),
            React.createElement(SimpleCard, { dataTestId: "SimpleCard-cm-v2-total", className: "", title: t('CREDIT_MEMO_TOTAL'), key: "credit-memo-total" },
                React.createElement("p", { className: "font-medium mt-1 text-gray-900 tracking-tight text-base" }, formatMoney(creditTotal)))),
        React.createElement("div", { className: "flex items-center gap-2 mt-4 mb-2" },
            React.createElement(Switch, { id: "rebill-toggle-v2", checked: rebillRequired, onCheckedChange: setRebillRequired }),
            React.createElement(Label, { htmlFor: "rebill-toggle-v2" }, t('REBILL'))),
        React.createElement("div", { className: "overflow-x-auto" },
            React.createElement(Table, null,
                React.createElement(TableHeader, null, renderHeaders()),
                React.createElement(TableBody, null, adjustedItemsDetails.map((item, index) => renderLineItem(index, item))),
                React.createElement(TableFooter, null, rebillRequired && renderRebillTotal())))));
    const renderFooter = () => (React.createElement("div", { className: "flex flex-col-reverse md:flex-row md:justify-end gap-2" },
        React.createElement("button", { className: "button secondary-action", onClick: onClose }, t('CANCEL')),
        React.createElement("button", { className: "button button-green", onClick: handleSave, disabled: disableSave }, titleize(t('SAVE')))));
    return (React.createElement(CargomaticModal, { dialogClassName: "modal-override modal-dialog md:w-4/5 w-full", title: t('CREATE_CREDIT_MEMO'), style: { top: '10%' }, show: showModal, onHide: onClose, body: renderModalBody(), footer: renderFooter() }));
}
